import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";
import { useState } from "react";
import { FaArrowDown, FaArrowUp, FaClock, FaWallet } from "react-icons/fa";
import { useSelector } from "react-redux";
import {
  createStripeAcc,
  createStripeAccLink,
  getPayoutsBalance,
  getPayoutsHistory,
  getStripeAcc,
  getStripeCountries,
} from "../actions/acc_actions";
import ContentLayout from "../components/layout/ContentLayout";

const { Option } = Select;

export default function Payout() {
  const accUrn = useSelector((state) => state.account.urn);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [form] = Form.useForm();

  const {
    isLoading: stripeAccLoading,
    data: stripeAccData,
    isError: stripeAccError,
    refetch: refetchStripeAcc,
  } = useQuery({
    queryKey: ["stripe-acc", accUrn],
    queryFn: () => getStripeAcc(accUrn),
    enabled: !!accUrn,
  });

  const {
    data: stripeCountriesData,
    isLoading: stripeCountriesLoading,
    refetch: refetchStripeCountries,
  } = useQuery({
    queryKey: ["stripe-countries"],
    queryFn: getStripeCountries,
    enabled: !!accUrn,
  });

  const stripeAcc = stripeAccData?.data || null;
  const stripeConnectedAccount = stripeAcc?.stripeConnectedAccount || null;
  const connectAccountId =
    stripeAcc?.connectAccountId || stripeConnectedAccount?.id || null;

  const {
    isLoading: balancesLoading,
    data: balancesData,
    refetch: refetchBalances,
  } = useQuery({
    queryKey: ["balances", accUrn],
    queryFn: () => getPayoutsBalance(accUrn),
    enabled: !!accUrn,
  });

  const { isLoading: transactionsLoading, data: transactionsData } = useQuery({
    queryKey: ["transactions", accUrn],
    queryFn: () => getPayoutsHistory(accUrn),
    enabled: !!accUrn,
  });

  const anyLoading =
    stripeAccLoading ||
    stripeCountriesLoading ||
    balancesLoading ||
    transactionsLoading;

  if (anyLoading) {
    return (
      <div className="wallet-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (stripeAccError) {
    return (
      <Alert
        type="error"
        message="Failed to load Stripe account"
        description="We couldn't load your Stripe account. Please try again or contact support."
      />
    );
  }

  // --- normalize some values for tables / cards ---
  const payoutsEnabled = stripeConnectedAccount?.payouts_enabled ?? false;
  const chargesEnabled = stripeConnectedAccount?.charges_enabled ?? false;

  const formatDate = (ts) => {
    try {
      if (!ts) return "-";
      // Stripe timestamps may be seconds
      const n = Number(ts);
      const d = n < 1e12 ? new Date(n * 1000) : new Date(n);
      return d.toLocaleString();
    } catch {
      return "-";
    }
  };

  const accountDetails = [
    { key: "Account ID", value: connectAccountId ?? "-" },
    {
      key: "Business name",
      value:
        stripeConnectedAccount?.business_profile?.name ??
        stripeConnectedAccount?.company?.name ??
        "-",
    },
    {
      key: "Business type",
      value: stripeConnectedAccount?.business_type ?? "-",
    },
    { key: "Email", value: stripeConnectedAccount?.email ?? "-" },
    { key: "Country", value: stripeConnectedAccount?.country ?? "-" },
    {
      key: "Default currency",
      value: stripeConnectedAccount?.default_currency ?? "-",
    },
    { key: "Charges enabled", value: chargesEnabled ? "Yes" : "No" },
    { key: "Payouts enabled", value: payoutsEnabled ? "Yes" : "No" },
    {
      key: "Details submitted (stripe)",
      value: stripeConnectedAccount?.details_submitted ? "Yes" : "No",
    },
    {
      key: "Requirements currently_due",
      value:
        (stripeConnectedAccount?.requirements?.currently_due || []).join(
          ", ",
        ) || "-",
    },
    {
      key: "Requirements past_due",
      value:
        (stripeConnectedAccount?.requirements?.past_due || []).join(", ") ||
        "-",
    },
    {
      key: "Requirements disabled_reason",
      value: stripeConnectedAccount?.requirements?.disabled_reason ?? "-",
    },
    {
      key: "TOS accepted at",
      value: formatDate(stripeConnectedAccount?.tos_acceptance?.date),
    },
    { key: "Created at", value: formatDate(stripeConnectedAccount?.created) },
  ];

  const externalAccountsList =
    stripeConnectedAccount?.external_accounts?.data || [];

  // Transactions mapping (if any)
  const transactions = (transactionsData || []).map((item) => ({
    id: item.id,
    productName: item.order_item?.product?.name_en || "-",
    type: item.transaction_type,
    amount: Number(item.amount),
    commission: Number(item.commission_amount),
    status: item.status,
    date: item.created_at
      ? new Date(item.created_at).toLocaleDateString()
      : "-",
  }));

  const accountDetailsColumns = [
    { title: "Field", dataIndex: "key", key: "key", width: 250 },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (v) => (v === "" || v == null ? "-" : v),
    },
  ];

  const externalAccountsColumns = [
    {
      title: "Type / Brand",
      dataIndex: "brand",
      key: "brand",
      render: (_, rec) => rec.object ?? "-",
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand2",
      render: (v) => v ?? "-",
    },
    {
      title: "Last4",
      dataIndex: "last4",
      key: "last4",
      render: (v) => v ?? "-",
    },
    {
      title: "Exp",
      dataIndex: "exp",
      key: "exp",
      render: (_, rec) =>
        rec.exp_month ? `${rec.exp_month}/${rec.exp_year}` : "-",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (v) => v ?? "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v) => v ?? "-",
    },
  ];

  const transactionColumns = [
    {
      title: "Product",
      dataIndex: "productName",
      responsive: ["md"],
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (v) =>
        v === "sale" ? (
          <Tag color="green">
            <FaArrowUp /> Sale
          </Tag>
        ) : (
          <Tag color="red">
            <FaArrowDown /> Refund
          </Tag>
        ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (v) => `$${v.toFixed(2)}`,
    },
    {
      title: "Transaction Fees",
      dataIndex: "commission",
      responsive: ["lg"],
      render: (v) => `$${v.toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => {
        const color =
          v === "pending_clearance"
            ? "orange"
            : v === "completed"
              ? "green"
              : "red";
        return <Tag color={color}>{(v || "").replace("_", " ")}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      responsive: ["md"],
    },
  ];

  // --- create Stripe account handler (modal submit) ---
  const handleCreateAccount = async (values) => {
    setCreateLoading(true);
    try {
      await createStripeAcc({ ...values, accountUrn: accUrn });
      await redirectToStripeAccountLink(accUrn);
      refetchStripeAcc();
    } catch (err) {
      console.error("createStripeAcc failed:", err);
      message.error("Failed to create Stripe account. Please try again.");
    } finally {
      setCreateLoading(false);
      setCreateModalVisible(false);
      form.resetFields();
    }
  };

  // helper to open stripe onboarding / account link
  const redirectToStripeAccountLink = async (accountUrnParam) => {
    try {
      const res = await createStripeAccLink({ accountUrn: accountUrnParam });
      const url = res?.data?.accountLink?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      message.error("Failed to get Stripe account link. Please try again.");
    } catch (err) {
      console.log("Failed to redirect to Stripe account link:", err);
      message.error("Failed to open Stripe onboarding. Please try again.");
    }
  };

  // --- Header / balances for top of page (when connected) ---
  const clearedBalance = balancesData?.balance?.clearedBalance ?? 0;
  const pendingBalance = balancesData?.balance?.pendingBalance ?? 0;

  return (
    <ContentLayout
      header={
        <>
          <h1>
            Wallet <br /> <span>Your Financial Overview</span>
          </h1>

          <Row gutter={[16, 16]} className="wallet-cards">
            <Col xs={24} sm={12} lg={8}>
              <Card className="wallet-card gradient-blue">
                <div className="card-content">
                  <FaWallet className="card-icon" />
                  <div>
                    <h3>Total Balance</h3>
                    <p>
                      $
                      {((clearedBalance ?? 0) + (pendingBalance ?? 0)).toFixed(
                        2,
                      )}
                    </p>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card className="wallet-card gradient-green">
                <div className="card-content">
                  <FaArrowUp className="card-icon" />
                  <div>
                    <h3>Cleared Balance</h3>
                    <p>${(clearedBalance ?? 0).toFixed(2)}</p>
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Card className="wallet-card gradient-orange">
                <div className="card-content">
                  <FaClock className="card-icon" />
                  <div>
                    <h3>Pending Balance</h3>
                    <p>${(pendingBalance ?? 0).toFixed(2)}</p>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      }
    >
      {/* Account overview / CTA area */}
      <Card style={{ marginBottom: 16 }}>
        {!connectAccountId ? (
          <>
            <h3>Connect your Stripe account</h3>
            <p>
              You need to create and connect your Stripe account to see balances
              and request payouts.
            </p>

            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <Button
                type="primary"
                onClick={() => setCreateModalVisible(true)}
              >
                Create Stripe account
              </Button>

              <Button
                onClick={() => {
                  Promise.all([refetchStripeAcc(), refetchStripeCountries()])
                    .then(() =>
                      message.success("Refreshed Stripe account status"),
                    )
                    .catch(() => message.error("Failed to refresh"));
                }}
              >
                Refresh status
              </Button>
            </div>
          </>
        ) : !!!connectAccountId ? (
          <>
            <Alert
              type="warning"
              message="Stripe account connected but not fully enabled"
              description={
                <div>
                  <p>
                    Your Stripe account is connected ({connectAccountId}) but
                    one or more required capabilities are not enabled:
                  </p>
                  <ul>
                    {!chargesEnabled && (
                      <li>• Payments (charges) not enabled</li>
                    )}
                    {!payoutsEnabled && (
                      <li>• Payouts not enabled — you must add bank details</li>
                    )}
                  </ul>
                  <p>
                    Click below to complete account verification and add bank
                    details in Stripe.
                  </p>
                </div>
              }
              showIcon
              style={{ marginBottom: 16 }}
            />

            <div style={{ display: "flex", gap: 12 }}>
              <Button
                type="primary"
                onClick={() => redirectToStripeAccountLink(accUrn)}
              >
                Complete Stripe verification
              </Button>

              <Button
                onClick={() => {
                  Promise.all([refetchStripeAcc(), refetchBalances()])
                    .then(() => message.success("Refreshed account & balances"))
                    .catch(() => message.error("Refresh failed"));
                }}
              >
                Refresh status
              </Button>
            </div>
          </>
        ) : (
          <div>
            <p>Your Stripe account is connected and enabled.</p>
            <div style={{ display: "flex", gap: 12 }}>
              {/* <Button
                onClick={() => {
                  refetchBalances();
                  refetchStripeAcc();
                  message.success("Refreshed");
                }}
              >
                Refresh balances & account
              </Button> */}
            </div>
          </div>
        )}

        {/* Modal for account creation */}
        <Modal
          title="Create Stripe account"
          open={createModalVisible}
          onCancel={() => {
            setCreateModalVisible(false);
            form.resetFields();
          }}
          onOk={() => form.submit()}
          confirmLoading={createLoading}
        >
          <Form form={form} layout="vertical" onFinish={handleCreateAccount}>
            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: "Please enter email" },
                { type: "email", message: "Invalid email" },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="country"
              label="Country"
              rules={[{ required: true, message: "Please select country" }]}
            >
              <Select
                placeholder="Select country"
                showSearch
                optionFilterProp="children"
              >
                {stripeCountriesData?.data?.countries?.map((c) => (
                  <Option key={c.code} value={c.code}>
                    {c.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Card>

      {/* Collapsed Account details & External accounts */}
      <Collapse defaultActiveKey={[]} style={{ marginTop: 16 }}>
        <Collapse.Panel header="Stripe Account Details" key="account-details">
          <Table
            columns={accountDetailsColumns}
            dataSource={accountDetails.map((r, idx) => ({
              ...r,
              key: `${r.key}-${idx}`,
            }))}
            pagination={false}
            showHeader={false}
            rowKey="key"
            size="small"
          />
        </Collapse.Panel>

        <Collapse.Panel header="External Accounts" key="external-accounts">
          {externalAccountsList.length ? (
            <Table
              columns={externalAccountsColumns}
              dataSource={externalAccountsList.map((a) => ({
                ...a,
                key: a.id,
              }))}
              pagination={false}
              rowKey="id"
              size="small"
            />
          ) : (
            <Empty description="No external accounts" />
          )}
        </Collapse.Panel>
      </Collapse>

      {/* Transactions table (always shown) */}
      <Card className="wallet-table-card" style={{ marginTop: 16 }}>
        <h3>Transactions</h3>
        <Table
          columns={transactionColumns}
          dataSource={transactions}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: 600 }}
        />
      </Card>
    </ContentLayout>
  );
}
